/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { AddSharp } from "@mui/icons-material";
import { Alert, Button, Snackbar } from "@mui/material";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { SalesOrderApiResponse } from "../../../@types/DRO/SalesOrderResponse";
import { SalesOrderApi } from "../../../api/OrderManagement/SalesOrderApi";
import { DownloadPdfApi } from "../../../api/PdfDownload/DownloadPdfApi";
import { UseDebounce } from "../../../states/Hooks/UseDebounce";
import SideBarNavigation from "../../Layouts/Sidebar/Sidebar";
import SalesOrderTableComponent from "../../Layouts/Table/SalesOrderTableComponent";
import RoleGuard from "../../auth/RoleGuard";
import CustomDropdown from "../CustomDropdown/CustomDropdown";

const salesOrderStatusOptions = [
  { value: "", label: "ALL" },
  { value: "PENDING", label: "Pending" },
  { value: "CLOSED", label: "Closed" },
];

const SalesOrderPage = () => {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const columns = [
    "id",
    "origin",
    "reference",
    "customerName",
    "cycleNo",
    "creationDate",
    "deliveryWarehouseName",
    "deliveryWarehouseCode",
    "status",
    "salesValue",
    "fleetName",
    "jobId",
    "jobType",
    "action",
    "download",
  ];
  const columnHeaders = [
    "Sales Order ID",
    "Origin",
    "Reference",
    "Customer Name",
    "Cycle No",
    "Creation Date",
    "Warehouse Name",
    "Warehouse Code",
    "Status",
    "Sales Value",
    "Fleet Name",
    "Job ID",
    "Job Type",
    "Action",
    "Download",
  ];
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [customerFilter, setCustomerFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState(
    () => searchParams.get("status") || ""
  );
  const [partCondition, setPartCondition] = useState("");
  const debouncedSearch = UseDebounce(search, 500);

  // Update URL when status filter changes
  useEffect(() => {
    const currentStatus = searchParams.get("status") || "";
    if (currentStatus === statusFilter && searchParams.size === (statusFilter ? 1 : 0)) return;

    if (statusFilter) {
      setSearchParams({ status: statusFilter });
    } else {
      setSearchParams({});
    }
  }, [statusFilter, searchParams, setSearchParams]);
  const debouncedCustomerFilter = UseDebounce(customerFilter, 500);
  const [totalPages, setTotalPages] = useState(1);
  const [salesOrders, setSalesOrders] = useState<
    SalesOrderApiResponse["data"]["data"]
  >([]);
  const [selectedOrder, setSelectedOrder] = useState<{
    id: number;
    status: string;
  } | null>(null);

  // Snackbar State
  const [error, setError] = useState<string | null>(null);
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, debouncedCustomerFilter, statusFilter, partCondition]);

  useEffect(() => {
    setIsLoading(true);
    setError(null);
    SalesOrderApi.api
      .getAllSalesOrders(
        page,
        pageSize,
        debouncedSearch,
        "SALES",
        statusFilter,
        debouncedCustomerFilter,
        partCondition
      )
      .then((response) => {
        setSalesOrders(response.data.data);
        setTotalPages(response.data.totalPages);
      })
      .catch((error) => {
        setError(
          error?.response?.data?.message ||
            error?.message ||
            "Failed to fetch sales orders. Please try again."
        );
        setOpenSnackbar(true);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [page, pageSize, debouncedSearch, statusFilter, debouncedCustomerFilter, partCondition]);

  const handleViewOrder = (orderId: number) => {
    navigate(`/sales-order/view/${orderId}${statusFilter ? `?status=${statusFilter}` : ''}`);
  };

  const handleDownloadPdf = async (orderId: number) => {
    try {
      await DownloadPdfApi.api.downloadSalesOrderPdf(orderId);
    } catch (error: any) {
      setError(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to download PDF. Please try again."
      );
      setOpenSnackbar(true);
    }
  };

  const formattedSalesOrders = salesOrders.map((order) => ({
    id: order.id,
    reference: order.reference,
    origin: order.origin || "-",
    customerName: order.customerName || "N/A",
    cycleNo: order.cycleNo || "-",
    creationDate: order.creationDate,
    deliveryWarehouseName:
      order.fromWarehouseDTO?.deliveryWarehouseName || "N/A",
    deliveryWarehouseCode:
      order.fromWarehouseDTO?.deliveryWarehouseCode || "N/A",
    status: order.status || "-",
    salesValue: order.salesValue || 0,
    fleetName: order.fleetName || "-",
    jobId: order.jobId || "-",
    jobType: order.jobType || "-",
    warning: order.warning || false,
    action: order.id,
    download: order.id,
  }));

  const handleSnackbarClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenSnackbar(false);
  };

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Sidebar */}
      <div className="w-64 flex-shrink-0 bg-white shadow-md">
        <SideBarNavigation />
      </div>

      {/* Main content area */}
      <div className="flex-1 flex flex-col overflow-hidden relative">
        <div className="flex-1 overflow-auto bg-[#F8FAFC]">
          <div className="p-6">
            <h1 className="text-3xl font-semibold mb-6 text-left">
              Sales Order Details
            </h1>

            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center space-x-4">
                <input
                  type="text"
                  placeholder="Search by Order ID, Origin"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg w-60 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                />
                <input
                  type="text"
                  placeholder="Search by Customer Name, Cycle No"
                  value={customerFilter}
                  onChange={(e) => setCustomerFilter(e.target.value)}
                  className="pl-3 pr-2 py-2 border border-gray-300 rounded-lg w-60 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                />
                <CustomDropdown
                  label="Status:"
                  options={salesOrderStatusOptions}
                  value={statusFilter}
                  onChange={setStatusFilter}
                  minWidth="120px"
                />
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-700">
                    Type:
                  </span>
                  <select
                    value={partCondition}
                    onChange={(e) => setPartCondition(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="">ALL</option>
                    <option value="WORKING">Working</option>
                    <option value="NOT_WORKING">Not Working</option>
                  </select>
                </div>
              </div>
              <div className="flex items-center gap-4 ml-4">
                 <RoleGuard deny={["ROLE_FRANCHISE_INVENTORY_MANAGER"]}>
                <Link to={`/sales-order/create${statusFilter ? `?status=${statusFilter}` : ""}`}>
                  <Button
                    variant="contained"
                    startIcon={<AddSharp />}
                    sx={{
                      textTransform: "none",
                      bgcolor: "#171F21",
                      "&:hover": { bgcolor: "#2a3a3c" },
                      fontSize: "14px",
                      borderRadius: "25px",
                      px: 3,
                      py: 1.5,
                      minWidth: "150px",
                      height: "40px",
                    }}>
                    Add Order
                  </Button>
                </Link>
                </RoleGuard>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <SalesOrderTableComponent
                  columns={columns}
                  columnHeaders={columnHeaders}
                  data={formattedSalesOrders}
                  setSelectedOrder={setSelectedOrder}
                  currentPage={page}
                  setCurrentPage={setPage}
                  totalPages={totalPages}
                  statusFilter={statusFilter}
                  onViewOrder={handleViewOrder}
                  onDownloadPdf={handleDownloadPdf}
                  isLoading={isLoading}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Snackbar for Error Handling */}
        <Snackbar
          open={openSnackbar}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}>
          <Alert
            onClose={handleSnackbarClose}
            severity="error"
            sx={{ width: "100%" }}
            variant="filled">
            {error}
          </Alert>
        </Snackbar>
      </div>
    </div>
  );
};

export default SalesOrderPage;
